import React, { useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ClientDto } from 'api/Clients/Types/Client'
import { PaymentMethodReplyDto } from 'api/PaymentProvider/Types/PaymentMethodReplyDto'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { TransactionsState } from 'api/Transaction/Types/TransactionsState'
import { WithdrawalFromWalletPspTransaction } from 'api/Transaction/Types/WithdrawalFromWalletPspTransaction'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import PaymentServiceProviderFields from 'components/PaymentServiceProviderFields/PaymentServiceProviderFields'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { isNotEmpty } from 'utils/is-empty'

const mapFieldsToDetails = (options: { [key: string]: string | number }) =>
  Object.keys(options).map((f) => ({
    name: f,
    value: options[f],
  }))

function dataToSubmit(
  values: WithdrawalFormValues,
  wallet: WalletDto
): FormSubmitValues<WithdrawalFromWalletPspTransaction> {
  return {
    amount: toNumber(values.amount),
    comment: values.comment,
    fromWalletId: values.walletId as string,
    details: mapFieldsToDetails(values.options),
    pspCurrencyId: values.currency || wallet.currency.id,
    toPspId: values.paymentProviderId as string,
    transactionStateId: values.transactionStateId,
  }
}

const WithdrawalFormUI: React.FC<FormikProps<WithdrawalFormValues> & OuterProps> = (props) => {
  const {
    client,
    handleSubmit,
    paymentProviderFields,
    isSubmitting,
    values,
    errors,
    setFieldValue,
    isLoading,
  } = props

  useEffect(() => {
    if (paymentProviderFields.length) {
      const options = paymentProviderFields.reduce((previousValue, currentValue) => {
        if (currentValue.mandatory) {
          return {
            ...previousValue,
            [currentValue.field]: currentValue.systemFilled ? currentValue.value : '',
          }
        }
        return previousValue
      }, {})
      setFieldValue('options', options)
    }
  }, [paymentProviderFields]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // reset form on change client
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Loading isLoading={isLoading}>
      <Form onSubmit={handleSubmit}>
        <DataGrid columns={1}>
          <DataColumn>
            <DataCard title='Withdrawal' columns={2}>
              <FormReadonlyField
                name='client'
                label='Client'
                value={values.client ?? ''}
                rightAddon={
                  <>
                    <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                      Select
                    </Button>
                    {client?.id && (
                      <Link
                        to={generatePath(links.recordsClients.detailsUrl as string, {
                          id: client.id,
                          slug: 'details',
                        })}
                        showIcon
                      />
                    )}
                  </>
                }
              />
              <FormSelectField
                name='walletId'
                label='Wallet'
                options={props.wallets.map((wallet) => ({
                  id: wallet.id,
                  name: `${wallet.name}-balance:${wallet.balance}`,
                }))}
                error={errors.walletId}
                onChange={(value) => {
                  const nextWalletId = value ? String(value) : ''
                  props.setFieldValue('walletId', nextWalletId)
                  props.setWallet(nextWalletId)
                }}
                rightAddon={
                  values.walletId && (
                    <Link
                      to={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: values.walletId,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )
                }
              />
              <FormSelectField
                name='paymentProviderId'
                label='Payment Provider'
                options={props.paymentProviders.map((paymentProvider) => ({
                  id: paymentProvider.method_id,
                  name: paymentProvider.method_name,
                }))}
                error={errors.paymentProviderId}
                onChange={(value) => {
                  const nextPaymentProviderId = value ? String(value) : ''
                  setFieldValue('paymentProviderId', nextPaymentProviderId)
                  props.getPaymentProviderFields(nextPaymentProviderId)
                }}
              />
              <FormField
                name='amount'
                label='Amount'
                type='number'
                error={errors.amount}
                rightAddon={
                  props.wallet ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {props.wallet.currency.id}
                    </span>
                  ) : undefined
                }
              />
              <PaymentServiceProviderFields
                paymentProviderFields={props.paymentProviderFields}
                errors={errors}
                setFieldValue={setFieldValue}
                setCurrency={props.setCurrency}
              />
              <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
              <FormSelectField
                name='transactionStateId'
                label='Transaction State'
                options={[
                  { id: 0, name: 'Default' },
                  ...props.transactionState
                    .filter((state) => state.id >= 0 && state.id <= 300)
                    .filter((state) => state.id !== 100),
                ]}
                error={errors.transactionStateId}
                onChange={(value) => setFieldValue('transactionStateId', value ? Number(value) : 0)}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
        <div className='mt-4 flex justify-center'>
          <Button disabled={isSubmitting} type='submit'>
            Submit
          </Button>
        </div>
      </Form>
    </Loading>
  )
}

export interface WithdrawalFormValues {
  client: string | null
  walletId: string | undefined
  amount: number
  comment: string
  paymentProviderId: string | undefined
  options: { [key: string]: string | number }
  transactionStateId: number | null
  currency: string | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | null
}

interface OuterProps {
  isLoading: boolean
  client: ClientDto | null
  wallets: WalletDto[]
  paymentProviders: PaymentMethodReplyDto[]
  paymentProviderFields: PaymentProviderFields[]
  transactionState: TransactionsState[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  wallet?: WalletDto
  paymentProviderId: string

  setForm(form: WithdrawalFormValues): void

  onSubmit(values: FormSubmitValues<WithdrawalFromWalletPspTransaction>): Promise<void>

  setClient(): void

  setCurrency(currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string): void

  getPaymentProviderFields(paymentProviderId: string): Promise<void>

  setWallet(walletId: string): void
}

export const WithdrawalForm = withFormik<OuterProps, WithdrawalFormValues>({
  mapPropsToValues: ({ client, wallet, currency, paymentProviderId }) => {
    const currentClient: Partial<ClientDto> = client || {}
    return {
      client: currentClient.name || null,
      options: {},
      walletId: wallet?.id,
      transactionStateId: 0,
      wallet,
      currency,
      paymentProviderId,
      amount: 0,
      comment: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      props.setForm(values)
      await props.onSubmit(dataToSubmit(values, props.wallet as WalletDto))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<WithdrawalFormValues> = {}
    if (values.transactionStateId == null) {
      errors.transactionStateId = 'Required'
    }
    if (!values.walletId) {
      errors.walletId = 'Required'
    }
    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }
    if (!values.paymentProviderId) {
      errors.paymentProviderId = 'Required'
    }
    if (isNotEmpty(values.options)) {
      Object.keys(values.options).forEach((key: string) => {
        if (values.options && !values.options[key]) {
          errors.options = {
            ...errors.options,
            [key]: 'Required',
          }
        }
      })
    }
    return errors
  },
  enableReinitialize: true,
})(WithdrawalFormUI)
